/**
 * LineItemsEditor - Shared component for editing line items in estimates and invoices
 * Includes typeahead search, quantity, price, tax checkbox, and add/remove functionality
 * 
 * Quantity handling:
 * - Empty by default (no value shown)
 * - Auto-fills to 1 when price is entered and qty is empty
 * - Supports decimals (e.g., 1.5 hours)
 * - inputMode="decimal" for proper mobile keyboard
 * 
 * Validation:
 * - Client-side validation for quantity (required, > 0, max 3 decimals)
 * - Client-side validation for price (required, >= 0, max 2 decimals)
 * - Inline error messages with red border
 */

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Plus, Trash } from "phosphor-react";
import LineItemInput from "./LineItemInput";

/**
 * Normalize line items BEFORE validation.
 * - If price is set and qty is empty, set qty to "1"
 * Returns a new array with normalized items.
 */
export function normalizeLineItems(items) {
  return items.map(item => {
    const hasPrice = item.price !== "" && item.price !== null && item.price !== undefined;
    const hasNoQty = item.quantity === "" || item.quantity === null || item.quantity === undefined;
    
    if (hasPrice && hasNoQty) {
      return { ...item, quantity: "1" };
    }
    return item;
  });
}

/**
 * Validate all line items before form submission.
 * IMPORTANT: Call normalizeLineItems() BEFORE validateLineItems() to avoid false qty errors.
 * Returns array of { index, field, error } for any invalid items.
 */
export function validateLineItems(items) {
  const errors = [];
  
  items.forEach((item, index) => {
    // Validate quantity
    const qty = item.quantity;
    if (qty === "" || qty === null || qty === undefined) {
      errors.push({ index, field: "quantity", error: "Quantity is required" });
    } else {
      const qtyNum = parseFloat(qty);
      if (isNaN(qtyNum)) {
        errors.push({ index, field: "quantity", error: "Quantity must be a number" });
      } else if (qtyNum <= 0) {
        errors.push({ index, field: "quantity", error: "Quantity must be greater than 0" });
      } else {
        // Check decimal places (max 3)
        const decimalPart = String(qty).split(".")[1];
        if (decimalPart && decimalPart.length > 3) {
          errors.push({ index, field: "quantity", error: "Maximum 3 decimal places allowed" });
        }
      }
    }
    
    // Validate price
    const price = item.price;
    if (price === "" || price === null || price === undefined) {
      errors.push({ index, field: "price", error: "Price is required" });
    } else {
      const priceNum = parseFloat(price);
      if (isNaN(priceNum)) {
        errors.push({ index, field: "price", error: "Price must be a number" });
      } else if (priceNum < 0) {
        errors.push({ index, field: "price", error: "Price cannot be negative" });
      } else {
        // Check decimal places (max 2)
        const decimalPart = String(price).split(".")[1];
        if (decimalPart && decimalPart.length > 2) {
          errors.push({ index, field: "price", error: "Maximum 2 decimal places allowed" });
        }
      }
    }
  });
  
  return errors;
}

export default function LineItemsEditor({
  items,
  onUpdateItem,
  onAddItem,
  onRemoveItem,
  onCatalogSelect,
  onFormatPrice,
  taxRatePercent = 0,
  testIdPrefix = "line-item",
  validationErrors = [], // Array of { index, field, error }
}) {
  // Handle quantity input - allow empty, integers, and decimals
  const handleQuantityChange = (index, value) => {
    // Allow empty string, or valid decimal numbers (including partial like "1." or ".5")
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      onUpdateItem(index, "quantity", value);
    }
  };

  // Format quantity on blur - normalize but keep as string for display
  const handleQuantityBlur = (index, value) => {
    if (value === "" || value === "." || value === "0") {
      // Keep empty - will be coerced to 1 on save
      onUpdateItem(index, "quantity", "");
    } else {
      // Parse and normalize the number
      const num = parseFloat(value);
      if (!isNaN(num) && num > 0) {
        // Keep reasonable precision (up to 3 decimal places)
        const formatted = num % 1 === 0 ? String(num) : num.toFixed(3).replace(/\.?0+$/, '');
        onUpdateItem(index, "quantity", formatted);
      } else {
        // Invalid - clear to empty
        onUpdateItem(index, "quantity", "");
      }
    }
  };

  // Helper to get error for a specific field
  const getFieldError = (index, field) => {
    const error = validationErrors.find(e => e.index === index && e.field === field);
    return error ? error.error : null;
  };

  return (
    <div>
      <Label className="mb-2 block">Line Items</Label>
      {/* Header row - desktop only */}
      <div className="hidden sm:grid grid-cols-12 gap-2 text-xs text-zinc-500 uppercase mb-1">
        <div className="col-span-6">Description</div>
        <div className="col-span-2 text-center">Qty</div>
        <div className="col-span-2">Price</div>
        <div className="col-span-1 text-center">Tax</div>
        <div className="col-span-1"></div>
      </div>
      <div className="space-y-2">
        {items.map((item, index) => {
          const qtyError = getFieldError(index, "quantity");
          const priceError = getFieldError(index, "price");
          
          return (
          <div key={index} className="bg-zinc-50 p-2 rounded-sm border border-zinc-100">
            {/* Mobile: Description label + input */}
            <div className="sm:hidden text-xs text-zinc-500 uppercase mb-1">Description</div>
            <div className="grid grid-cols-12 gap-2 items-center">
              {/* Description - full width on mobile, 6 cols on desktop */}
              <div className="col-span-12 sm:col-span-6">
                <LineItemInput
                  value={item.description}
                  onChange={(val) => onUpdateItem(index, "description", val)}
                  onSelect={(catalogItem) => onCatalogSelect(index, catalogItem)}
                  placeholder="Start typing to search..."
                  className="h-10 border-2"
                  data-testid={`${testIdPrefix}-description-${index}`}
                />
              </div>
              
              {/* Mobile: Row with labels for qty/price/tax */}
              <div className="col-span-4 sm:hidden text-xs text-zinc-500 uppercase">Qty</div>
              <div className="col-span-4 sm:hidden text-xs text-zinc-500 uppercase">Price</div>
              <div className="col-span-3 sm:hidden text-xs text-zinc-500 uppercase text-center">Tax</div>
              <div className="col-span-1 sm:hidden"></div>
              
              {/* Quantity - allows decimals */}
              <div className="col-span-4 sm:col-span-2">
                <Input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  value={item.quantity}
                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                  onBlur={(e) => handleQuantityBlur(index, e.target.value)}
                  placeholder="1"
                  className={`h-10 border-2 text-center ${qtyError ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                  data-testid={`${testIdPrefix}-qty-${index}`}
                  style={{
                    // Prevent iOS zoom on focus
                    fontSize: '16px'
                  }}
                />
                {qtyError && (
                  <p className="text-xs text-red-500 mt-1" data-testid={`${testIdPrefix}-qty-error-${index}`}>
                    {qtyError}
                  </p>
                )}
              </div>
              
              {/* Price */}
              <div className="col-span-4 sm:col-span-2 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" style={{ top: priceError ? "20px" : "50%" }}>$</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={item.price}
                  onChange={(e) => onUpdateItem(index, "price", e.target.value)}
                  onBlur={() => onFormatPrice(index)}
                  onFocus={(e) => {
                    if (e.target.value === "0.00" || e.target.value === "0") {
                      onUpdateItem(index, "price", "");
                    }
                  }}
                  placeholder="0.00"
                  className={`h-10 border-2 pl-7 ${priceError ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                  data-testid={`${testIdPrefix}-price-${index}`}
                  style={{
                    // Prevent iOS zoom on focus
                    fontSize: '16px'
                  }}
                />
                {priceError && (
                  <p className="text-xs text-red-500 mt-1" data-testid={`${testIdPrefix}-price-error-${index}`}>
                    {priceError}
                  </p>
                )}
              </div>
              
              {/* Tax checkbox - 44px tap target for iOS */}
              <div className="col-span-3 sm:col-span-1 flex items-center justify-center">
                <Checkbox
                  checked={item.is_taxable}
                  onCheckedChange={(checked) => onUpdateItem(index, "is_taxable", checked)}
                  disabled={taxRatePercent === 0}
                  data-testid={`${testIdPrefix}-taxable-${index}`}
                />
              </div>
              
              {/* Delete button */}
              <div className="col-span-1 flex items-center justify-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveItem(index)}
                  className="text-red-500 h-10 w-10"
                  disabled={items.length === 1}
                >
                  <Trash size={18} />
                </Button>
              </div>
            </div>
          </div>
        );
        })}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onAddItem} className="mt-2 border-2">
        <Plus size={16} className="mr-1" />
        Add Line Item
      </Button>
    </div>
  );
}
